import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { useHistory } from "react-router-dom";
import appRoutes from "../../routes/appRoutes";
import { CircularProgress } from "@material-ui/core";
import DEFAULT_PIC from "../../assets/images/default-picture.png";

import { getPostsListApiRoute } from "../../apiRoutes";

import Label from "../../components/label";

import { converToUtc, getFormattedDate } from "../../utils/dateTimeHelper";

const ViewForumList = props => {
  const history = useHistory(); // go to other page with or without data
  const observer = useRef(); // to check it is the last element or not - if it is last element then call an API
  const [pageNo, setPageNumber] = useState(1); // just set page number if needed in future.
  const [nextPage, setNextPage] = useState(false); // set next page if there is more data
  const [loading, setLoading] = useState(true); // loading variable used to reduce multiple API calls
  const [progressBar, setProgressBar] = useState(false); // progress bar hide and show
  const [limit, setLimit] = useState(10); // limit is fixed but if we need to change in future then change it here
  const [skip, setSkip] = useState(0); // set Skip page
  const [noMoreData, setNoMoreData] = useState(false); // no data found block hide-show

  // Data to be appended
  const [rows, setRows] = useState([]); // setting rows

  /*
   * Go to Post details page - need to pass _id to get the data on page
   */
  const goTo = no => {
    history.push(appRoutes.forumPostDetails + "/" + no);
  };

  /*
   * Check last element is visible or not on scroll
   */
  const lastElementRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(data => {
      if (data) {
        if (data[0].isIntersecting && nextPage && loading) {
          setLoading(false);
          if (loading) {
            let skipCalculate = limit * pageNo,
              updatePage = pageNo + 1;
            setPageNumber(pageNo => pageNo + 1);
            setSkip(skipCalculate);
            getForumPostsList(limit, skipCalculate, updatePage);
          }
          setTimeout(() => {
            setLoading(true);
          }, 2000);
        }
      }
    });
    if (node) observer.current.observe(node);
  });

  /*
   * Main function to call an API
   * Pass parameters compulusry (limit, skipPage, currentPage) to calculate pagination
   */

  const getForumPostsList = useCallback(
    (limitPage, skipPage, pageNoUpdated) => {
      let prm = {
        limit: limitPage,
        skip: skipPage,
        userId: props.user ? props.user.userData._id : "",
        userType: "Doctor",
        getMyPosts: props.myPostsCheckbox
      };

      if (props.startDate && props.endDate) {
        prm.startDate = converToUtc(props.startDate);
        prm.endDate = new Date(
          new Date(props.endDate).getFullYear(),
          new Date(props.endDate).getMonth(),
          new Date(props.endDate).getDate() + 1,
          0,
          0,
          0
        ).toISOString();
      }

      if (props.textSearch.trim().length > 0) {
        prm.text = props.textSearch.trim();
      }

      console.log("Now getting posts: ", props);

      let apiCallParam = {
        reqType: "GET",
        reqURL: getPostsListApiRoute,
        dataObj: {},
        params: prm,
        headers: {},
        successCallback: res => {
          // calculate is there any next page or not ?
          let postNotecount = res.data.data.count;
          let currentRowCount = postNotecount - pageNoUpdated * limitPage;
          if (currentRowCount > 0) {
            setNextPage(true);
          } else {
            setNextPage(false);
            setNoMoreData(true);
            setProgressBar(false);
          }
          // set the data
          let postData = [];
          res.data.data.posts.forEach(item => {
            let dt = {};

            dt._id = item._id;
            dt.topic =
              item.topic.length > 40
                ? item.topic.slice(0, 40) + "..."
                : item.topic;
            dt.message =
              item.message.length > 40
                ? item.message.slice(0, 40) + "..."
                : item.message;
            dt.tags = item.tags;
            dt.readBy= item.readBy;
            dt.unreadComments = item.unreadComments;
            dt.unreadReplyComments = item.unreadReplyComments;
            dt.createdAt = getFormattedDate(item.createdAt);

            if (item.commentsCount) {
              dt.commentsCount = item.commentsCount;
            } else {
              dt.commentsCount = 0;
            }

            // dt.fullName = item.userInfo.fullName;
            dt.fullName = '';
            dt.profilePic = DEFAULT_PIC;
            if (item.adminId) {
              dt.fullName = `${item.adminId.firstName || ''} ${item.adminId.lastName}`;
              dt.profilePic = item.adminId.profilePic.original;
            } else if (item.doctorId) {
              dt.fullName = `${item.doctorId.firstName || ''} ${item.doctorId.lastName}`;
              dt.profilePic = item.doctorId.profilePic.original;
            } else {
              dt.fullName = item.userInfo.fullName; // this needed just incase for old data
              dt.profilePic = item.userInfo.profilePic; // this needed just incase for old data
            }

            // dt.profilePic = item.userInfo.profilePic && item.userInfo.profilePic.original ? item.userInfo.profilePic.original : item.userInfo.profilePic;

            postData.push(dt);
          });
          if (skipPage === 0) {
            setRows(postData);
          } else {
            setRows(prev => prev.concat(postData));
          }
        },
        failureCallback: err => {
          console.log("Something went wrong. Please try again ", err);
        },
        showPopUp: false
      };
      props.makeAPICall(apiCallParam);
    }
  );

  /*
   * Watch on filter  by dates
   */
  useEffect(() => {
    resetPagination();
    setRows([]);
    getForumPostsList(limit, 0, 1);
  }, [props.filterByDateCall, props.myPostsCheckbox, props.doTextSearch]);

  /*
   * Reset pagination
   */
  const resetPagination = () => {
    setPageNumber(1);
    setNextPage(false);
    setLoading(true);
    setProgressBar(true);
    setSkip(0);
    setNoMoreData(false);
  };

  function shouldHighlightRow(row) {
    return !row.readBy?.includes(props.user.userData._id);
  }

  function getRowClass(row) {
    return shouldHighlightRow(row) ? "table-element-highlight cursor-pointer" : "cursor-pointer";
  }

  function shouldHighlightCommentsCell(row) {
    return !!row.unreadComments?.unreadComments || !!row.unreadReplyComments?.unreadComments;
  }

  function getCommentsCellClass(row, lastRow) {
    return (lastRow
      ? ""
      : " ") +
      (shouldHighlightCommentsCell(row) ? "table-element-highlight cursor-pointer" : "cursor-pointer");
  }

  return (
    <Fragment>
      <Paper className="w-100 consult-history-list-parent">
        <TableContainer
          className="forum-table-container primary-table-color"
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "20%" }}>Topic</TableCell>
                <TableCell style={{ width: "20%" }}>Preview</TableCell>
                <TableCell style={{ width: "20%" }}>Date Posted</TableCell>
                <TableCell style={{ width: "15%" }}>Comments</TableCell>
                <TableCell style={{ width: "25%", textAlign: "center" }}>
                  Posted By
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                /*
                 * If it is last element then take the referent to call an API Else print another block
                 */
                if (rows.length === index + 1) {
                  return (
                    <TableRow
                      className={getRowClass(row)}
                      ref={lastElementRef}
                      hover
                      key={row._id}
                      onClick={event => goTo(row._id)}
                    >
                      <TableCell
                        className={
                          (noMoreData && !rows.length) ||
                          (noMoreData && rows.length === limit) ||
                          (noMoreData && pageNo > 1)
                            ? ""
                            : ""
                        }
                      >
                        {row.topic}
                      </TableCell>

                      <TableCell
                        className={
                          (noMoreData && !rows.length) ||
                          (noMoreData && rows.length === limit) ||
                          (noMoreData && pageNo > 1)
                            ? ""
                            : ""
                        }
                      >
                        <span dangerouslySetInnerHTML={{__html: row.message}} />
                      </TableCell>

                      <TableCell
                        className={
                          (noMoreData && !rows.length) ||
                          (noMoreData && rows.length === limit) ||
                          (noMoreData && pageNo > 1)
                            ? ""
                            : ""
                        }
                      >
                        {row.createdAt}
                      </TableCell>

                      <TableCell className={getCommentsCellClass(row, (noMoreData && !rows.length) ||
                        (noMoreData && rows.length === limit) ||
                        (noMoreData && pageNo > 1))}>
                        {row.commentsCount}&nbsp;&nbsp;&nbsp;
                        <i className="far fa-comment primary-color"></i>
                      </TableCell>

                      <TableCell
                        className={
                          (noMoreData && !rows.length) ||
                          (noMoreData && rows.length === limit) ||
                          (noMoreData && pageNo > 1)
                            ? "ic-patient-pic text-center"
                            : " ic-patient-pic text-center"
                        }
                      >
                        <img
                          src={row.profilePic}
                          alt="user-ic-patient"
                          className="patient-image rounded-circle border mb-1"
                        />
                        <br /> <span className="ml-1 pt-1">{row.fullName}</span>
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  return (
                    <TableRow
                      className={getRowClass(row)}
                      hover
                      key={row._id}
                      onClick={event => goTo(row._id)}
                    >
                      <TableCell>{row.topic}</TableCell>

                      <TableCell><span dangerouslySetInnerHTML={{__html: row.message}} /></TableCell>

                      <TableCell>{row.createdAt}</TableCell>

                      <TableCell className={getCommentsCellClass(row, false)}>
                        {row.commentsCount}&nbsp;&nbsp;&nbsp;
                        <i className="far fa-comment primary-color"></i>
                      </TableCell>

                      <TableCell className="ic-patient-pic text-center">
                        <img
                          src={row.profilePic}
                          alt="user-ic-patient"
                          className="patient-image rounded-circle border mb-1"
                        />
                        <br /> <span className="ml-1 pt-1">{row.fullName}</span>
                      </TableCell>
                    </TableRow>
                  );
                }
              })}

              {/*
               * Progress bar on loading of APIs
               */}

              <TableRow className={progressBar ? "" : "d-none"}>
                <TableCell
                  colSpan={7}
                  className="text-center progress-bar-center"
                >
                  <CircularProgress disableShrink className="primary-loader" />
                </TableCell>
              </TableRow>

              {/*
               * No data fount if there are no data array in API
               */}

              <TableRow
                className={
                  (noMoreData && !rows.length) ||
                  (noMoreData && rows.length === limit) ||
                  (noMoreData && pageNo > 1)
                    ? ""
                    : "d-none"
                }
              >
                <TableCell
                  colSpan={7}
                  className="text-center progress-bar-center  no-data-found"
                >
                  <Label title="No data found" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Fragment>
  );
};

export default ViewForumList;
